'use client'

import { IDocument } from '@/shared/api/documents'
import { FilterDropdown, FilterOption } from '@/shared/components/FilterDropdown'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { FormDialog } from '@/shared/components/dialogs/FormDialog'
import { UploadDialogButton } from '@/shared/components/documents/UploadDialogButton'
import ViewDocumentDialog from '@/shared/components/documents/ViewDocumentDialog'
import { EditReferenceDocumentDialog } from '@/shared/components/documents/EditReferenceDocumentDialog'
import { MASTER_TYPE_SLUG } from '@/shared/constants/master-types'
import useGetMasterTypeCatalog from '@/shared/hooks/categories/masterTypes/useGetMasterTypeDetail'
import useDownLoadFile from '@/shared/hooks/documents/useDownLoadFile'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useMasterType } from '@/shared/hooks/useMasterType'
import { api } from '@/shared/lib/api'
import { useCan } from '@/shared/lib/authorization'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { formatBytes } from '@/shared/utils/index.utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { createColumnHelper } from '@tanstack/react-table'
import { Button } from '@workspace/ui/components/Button'
import { DataTable, DataTableSorting } from '@workspace/ui/components/DataTable'
import { Menu, MenuHeader, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { toast } from '@workspace/ui/components/Sonner'
import dayjs from 'dayjs'
import { MoreHorizontal } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PreviewSearchItem } from '@/shared/components/previewSearchItem'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    fileFormat: z.string().optional(),
    documentType: z.string().optional(),
    createdBy: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
    searchId: z.string().optional(),
    type: z.string().optional(),
    documentId: z.string().optional(),
})

export const Route = createFileRoute('/documents/')({
    validateSearch: searchSchema,
    component: DocumentsPage,
    beforeLoad: requirePermissionBeforeLoad('view', 'library'),
})

const columnHelper = createColumnHelper<Document>()

const documentFormatOptions: FilterOption[] = [
    {
        id: 'PDF',
        name: 'PDF',
    },
    {
        id: 'IMG',
        name: 'IMG',
    },
    {
        id: 'EXCEL',
        name: 'EXCEL',
    },
    {
        id: 'WORD',
        name: 'WORD',
    },
    {
        id: 'OTHER',
        name: 'OTHER',
    },
]

function DocumentsPage() {
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const searchQuery = searchParams.q || ''
    const page = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const documentFormat = searchParams.fileFormat ? searchParams.fileFormat.split(',') : []
    const documentTypeFilters = searchParams.documentType ? searchParams.documentType.split(',') : []
    const creatorFilters = searchParams.createdBy ? searchParams.createdBy.split(',') : []
    const sorting = searchParams.sortBy
        ? { id: searchParams.sortBy, desc: searchParams.sortDirection === 'desc' }
        : null

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setDocumentFormat = (format: string[]) =>
        updateSearch({ fileFormat: format.length > 0 ? format.join(',') : undefined, page: 1 })
    const setDocumentTypeFilters = (type: string[]) =>
        updateSearch({ documentType: type.length > 0 ? type.join(',') : undefined, page: 1 })
    const setCreatorFilters = (creator: string[]) =>
        updateSearch({ createdBy: creator.length > 0 ? creator.join(',') : undefined, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.id, sortDirection: sort.desc ? 'desc' : 'asc', page: 1 })
        }
    }

    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
    const [isViewOpen, setIsViewOpen] = useState(false)
    const [viewDoc, setViewDoc] = useState<IDocument | null>(null)
    const [deleteDoc, setDeleteDoc] = useState<string | boolean>(false)
    const [editDocId, setEditDocId] = useState<string | null>(null)
    const [isEditOpen, setIsEditOpen] = useState(false)

    const [openCreatorFilter, setOpenCreatorFilter] = useState(false)
    const [openTypeDocumentFilter, setOpenTypeDocumentFilter] = useState(false)

    const { data: masterType } = useMasterType({
        enabled: openTypeDocumentFilter,
    })
    const { data: listDocumentType } = useGetMasterTypeCatalog({
        masterTypeId: masterType?.data?.find(mt => mt.code === MASTER_TYPE_SLUG.DOCUMENT_TYPE)?.id || '',
        page: 1,
        pageSize: 100,
        enabled: openTypeDocumentFilter,
    })

    const typeDocumentFilters = useMemo(
        () =>
            listDocumentType?.data?.map(x => ({
                id: x.id,
                name: x.name,
            })) || [],
        [listDocumentType],
    )

    const { data: usersData } = useQuery({
        queryKey: ['users'],
        queryFn: () => api.user.listAll({ page: 1, take: 100 }),
        enabled: openCreatorFilter,
    })

    const downloadMutation = useDownLoadFile()

    const creatorOptions = useMemo(
        () =>
            usersData?.data?.map(x => ({
                id: x.id,
                name: x.fullName,
            })) || [],
        [usersData],
    )

    const debouncedSearch = useDebounce(searchQuery, 300)

    const { data, isFetching, refetch } = useQuery({
        queryKey: [
            'documents',
            debouncedSearch,
            documentFormat,
            documentTypeFilters,
            creatorFilters,
            page,
            pageSize,
            sorting,
        ],
        queryFn: () =>
            api.document.list({
                page,
                take: pageSize,
                q: debouncedSearch || undefined,
                order: sorting ? (sorting.desc ? 'DESC' : 'ASC') : undefined,
                orderBy: sorting?.id,
                fileFormat: documentFormat?.join(',') || undefined,
                documentType: documentTypeFilters?.join(',') || undefined,
                createdBy: creatorFilters?.join(',') || undefined,
                withoutProject: true,
            }),
    })
    const documents = data?.data ?? []

    const totalResults = data?.meta?.itemCount ?? 0
    const totalPages = Math.ceil((data?.meta?.itemCount || 0) / pageSize)

    const deleteDocument = useMutation({
        mutationFn: async ({ ids }: { ids: string[] }) => {
            const promises = ids.map(id => api.document.delete(id))
            await Promise.all(promises)
            return ids
        },
        onError: error => {
            toast.error({
                title: error.message || t('documents.list.deleteError.description'),
            })
            throw error
        },
    })

    const handleDocumentTypeChange = (values: string[]) => {
        setDocumentTypeFilters(values)
    }

    const handleCreatorChange = (values: string[]) => {
        setCreatorFilters(values)
    }

    const selectedCount = Object.keys(rowSelection).length
    const selectedIds = Object.keys(rowSelection)

    const handleDeleteConfirm = async () => {
        if (!deleteDoc) return
        await deleteDocument.mutateAsync({ ids: [deleteDoc as string] })
        toast.success({
            title: t('documents.list.deleteSuccess.title'),
            description: t('documents.list.deleteSuccess.description'),
        })
        setDeleteDoc(false)
    }

    const handleBulkDeleteConfirm = async () => {
        await deleteDocument.mutateAsync({ ids: selectedIds })
        toast.success({
            title: 'Xóa tài liệu thành công',
            description: `Đã xóa ${selectedCount} tài liệu.`,
        })
        setRowSelection({})
    }

    const handleKeydown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            if (page !== 1) {
                setPage(1)
            } else {
                await refetch()
            }
        }
    }

    const columns = [
        columnHelper.display({
            id: 'actions1',
            header: '',
            size: 80,
            enableSorting: false,
            meta: {
                className: '!max-w-[46px] !min-w-[46px] !w-[46px]',
            },
            cell: ({ row }) => {
                const document = row.original

                return (
                    <MenuTrigger>
                        <Button variant="outline" size="iconSm" className="rounded-full border-foreground w-5 h-5">
                            <MoreHorizontal className="size-3!" />
                        </Button>
                        <MenuPopover className="min-w-[120px]">
                            <Menu>
                                <MenuHeader className="text-foreground">{t('common.actions.operation')}</MenuHeader>
                                <MenuItem
                                    onAction={() => {
                                        setViewDoc(document)
                                        setIsViewOpen(true)
                                    }}
                                    className="cursor-pointer"
                                >
                                    {t('common.actions.view')}
                                </MenuItem>
                                <MenuItem
                                    onAction={() => {
                                        setEditDocId(document.id)
                                        setIsEditOpen(true)
                                    }}
                                    className="cursor-pointer"
                                    isDisabled={!useCan('operate', 'library')}
                                >
                                    Sửa
                                </MenuItem>
                                <MenuItem
                                    onAction={() =>
                                        downloadMutation.mutateAsync({
                                            docId: document.id,
                                            fileId: document?.attachments?.[0]?.id,
                                        })
                                    }
                                    className="cursor-pointer"
                                >
                                    {t('common.actions.download')}
                                </MenuItem>
                                <MenuItem
                                    onAction={() => setDeleteDoc(document.id)}
                                    className="cursor-pointer text-destructive"
                                    isDisabled={!useCan('operate', 'library')}
                                >
                                    {t('common.actions.delete')}
                                </MenuItem>
                            </Menu>
                        </MenuPopover>
                    </MenuTrigger>
                )
            },
        }),
        columnHelper.accessor('title', {
            header: t('documents.list.table.document'),
            enableSorting: true,
            size: 240,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: ({ row }) => {
                const document = row.original
                return (
                    <div
                        onClick={() => {
                            setViewDoc(document)
                            setIsViewOpen(true)
                        }}
                        className="text-sm truncate cursor-pointer"
                    >
                        {document.title || '-'}
                    </div>
                )
            },
        }),
        columnHelper.accessor('documentType.name', {
            header: t('documents.list.table.documentType'),
            enableSorting: false,
            size: 120,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: ({ getValue }) => {
                const value = getValue() as string
                return <div className="text-sm truncate">{value || '-'}</div>
            },
        }),
        columnHelper.accessor('fileFormat', {
            header: t('documents.list.table.fileType'),
            enableSorting: false,
            size: 100,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: ({ getValue }) => {
                const value = getValue() as string
                return <div className="text-sm truncate">{value || '-'}</div>
            },
        }),
        columnHelper.accessor('fileSize', {
            header: t('documents.list.table.size'),
            enableSorting: false,
            size: 100,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: ({ getValue }) => {
                const value = getValue()
                return <div className="text-sm">{value > 0 ? formatBytes(value) : '-'}</div>
            },
        }),
        columnHelper.accessor('versionNumber', {
            header: t('documents.list.table.version'),
            enableSorting: false,
            size: 100,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: ({ getValue }) => {
                const value = getValue()
                return <div className="text-sm truncate">{value}</div>
            },
        }),
        columnHelper.accessor('description', {
            header: t('documents.list.table.description'),
            enableSorting: false,
            size: 250,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: ({ getValue }) => {
                const value = getValue() as string
                return (
                    <div className="line-clamp-2 text-sm whitespace-normal max-w-[250px]" title={value}>
                        {value}
                    </div>
                )
            },
        }),
        columnHelper.accessor('createdBy', {
            header: t('documents.list.table.creator'),
            enableSorting: false,
            size: 150,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: row => {
                const value = row.getValue()
                return <div className="text-sm truncate">{value?.fullName || '-'}</div>
            },
        }),
        columnHelper.accessor('updatedAt', {
            header: t('documents.list.table.lastModified'),
            enableSorting: true,
            size: 160,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
            },
            cell: ({ getValue }) => {
                const value = getValue()
                return value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
            },
        }),
    ]

    return (
        <div className="flex flex-col gap-4 max-lg:gap-2 h-[calc(100vh-150px)] min-h-[500px] overflow-hidden">
            <div className="shrink-0 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">{t('documents.list.title')}</h1>
                </div>
                <div className="flex gap-2">
                    <UploadDialogButton disabled={!useCan('operate', 'library')} />
                </div>
            </div>

            <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                <div className="shrink-0 flex gap-4 flex-wrap items-center max-lg:w-full max-lg:gap-2">
                    <BsSearchField
                        autoTrim
                        className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                        placeholder={t('documents.list.searchPlaceholder')}
                        value={searchQuery}
                        containerClassName={'max-lg:w-full'}
                        onChange={e => setSearchQuery(e)}
                        onKeyDown={handleKeydown}
                    />
                    <FilterDropdown
                        placeholder={t('documents.list.filterDocumentType')}
                        inputPlaceholder={t('documents.list.filterDocumentType')}
                        options={typeDocumentFilters}
                        selectedValues={documentTypeFilters}
                        onChange={handleDocumentTypeChange}
                        setOpen={setOpenTypeDocumentFilter}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                    <FilterDropdown
                        placeholder={t('documents.list.filterFileType')}
                        inputPlaceholder={t('documents.list.filterFileType')}
                        options={documentFormatOptions}
                        selectedValues={documentFormat}
                        onChange={value => setDocumentFormat(value)}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                    <FilterDropdown
                        placeholder={t('documents.list.filterCreator')}
                        inputPlaceholder={t('documents.list.filterCreator')}
                        options={creatorOptions}
                        selectedValues={creatorFilters}
                        onChange={handleCreatorChange}
                        setOpen={setOpenCreatorFilter}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                </div>

                <DataTable
                    columns={columns}
                    data={documents}
                    enableRowSelection
                    rowSelection={rowSelection}
                    enableSorting={true}
                    sorting={sorting}
                    setSorting={setSorting}
                    setRowSelection={setRowSelection}
                    isLoading={isFetching}
                    emptyString={t('common.emptyState')}
                    containerClassName="flex-1 overflow-y-auto min-h-0"
                />

                <div className="shrink-0">
                    <TableDataFooter
                        selectedCount={selectedCount}
                        onDelete={handleBulkDeleteConfirm}
                        deleteDisabled={selectedCount === 0}
                        totalCount={totalResults}
                        currentPage={page}
                        pageSize={pageSize}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={size => setPageSize(size || 10)}
                        isLoading={isFetching}
                    />
                </div>
            </div>

            <ViewDocumentDialog open={isViewOpen} onOpenChange={setIsViewOpen} document={viewDoc} />

            <EditReferenceDocumentDialog open={isEditOpen} onOpenChange={setIsEditOpen} documentId={editDocId} />

            <FormDialog
                open={!!deleteDoc}
                onOpenChange={setDeleteDoc}
                title={t('documents.list.deleteDialog.title')}
                description={t('documents.list.deleteDialog.description')}
                primaryActionLabel={t('common.actions.delete')}
                onPrimaryAction={handleDeleteConfirm}
                primaryActionVariant="destructive"
                isLoading={deleteDocument.isPending}
                contentClassName="text-center"
                descriptionClassName="text-secondary-foreground"
            />

            <PreviewSearchItem />
        </div>
    )
}
